import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckCircle, Circle, FileText, Image, ExternalLink, Download, Paperclip, AlertCircle, Sparkles, XCircle, Package, ClipboardList, Check } from 'lucide-react';
import { TaskWithDetails, completeTask } from '@/services/taskService';
import { taskCompletionService, TaskCompletion } from '@/firebase/taskCompletionService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { formService, Form } from '@/firebase/formService';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { complianceService, ComplianceTemplate, ReferenceDocument } from '@/firebase/complianceService';
import { assetService, Asset } from '@/firebase/assetService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { userService } from '@/firebase/database';
import { trackTask } from '@/utils/analytics';
import { isAdminInOrganization, isManagerInOrganization, fetchUserRoles } from '@/utils/roleHelpers';
import ImageGallery from './ImageGallery';
import CompleteTaskModal, { TaskCompletionData } from './CompleteTaskModal';
import ReviewTaskModal from './ReviewTaskModal';
import type { ComplianceTask } from '@/services/complianceTaskService';

interface ViewTaskModalProps {
  task: TaskWithDetails;
  onClose: () => void;
  /** When true, shows Complete and Review action buttons for pending tasks */
  showActions?: boolean;
  /** Called when task is completed or reviewed - parent can refresh task list */
  onTaskUpdated?: () => void;
}

const ViewTaskModal: React.FC<ViewTaskModalProps> = ({ task, onClose, showActions = false, onTaskUpdated }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  // Task completion data
  const [completion, setCompletion] = useState<TaskCompletion | null>(null);
  const [isLoadingCompletion, setIsLoadingCompletion] = useState(true);
  
  // Checklist run history states (kept for detail panel, but not displayed in main view)
  const [checklistRuns, setChecklistRuns] = useState<ChecklistCompletion[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [templateChecklist, setTemplateChecklist] = useState<Checklist | null>(null);
  const [selectedRun, setSelectedRun] = useState<ChecklistCompletion | null>(null);
  
  // Asset-related states
  const [relatedAssets, setRelatedAssets] = useState<Asset[]>([]);
  const [assetCompletions, setAssetCompletions] = useState<{
    assetId: string;
    assetName: string;
    checklistCompletions: ChecklistCompletion[];
    formCompletions: FormCompletion[];
  }[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  
  // Template and reference documents
  const [template, setTemplate] = useState<ComplianceTemplate | null>(null);
  const [referenceDocuments, setReferenceDocuments] = useState<ReferenceDocument[]>([]);

  // Action modals and permissions (when showActions is true)
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [canReviewTasks, setCanReviewTasks] = useState(false);

  useEffect(() => {
    if (showActions && user?.id && currentOrganization?.id) {
      const orgId = currentOrganization.id;
      const loadCanReview = async () => {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const isAdmin = isAdminInOrganization(userData, orgId);
            const isManager = isManagerInOrganization(userData, orgId);
            setCanReviewTasks(isAdmin || isManager);
          } else {
            const roles = await fetchUserRoles(user.id);
            setCanReviewTasks(roles.isSystemManager || roles.systemRoles.includes('admin'));
          }
        } catch {
          setCanReviewTasks(false);
        }
      };
      loadCanReview();
    }
  }, [showActions, user?.id, currentOrganization?.id]);

  useEffect(() => {
    loadCompletionData();
    loadChecklistHistory();
    loadTemplateReferenceDocuments();
    loadRelatedAssets();
    // Track task view
    trackTask.viewed(task.id);
  }, [task.id]);

  const loadCompletionData = async () => {
    setIsLoadingCompletion(true);
    try {
      const result = await taskCompletionService.getCompletionsForTask(task.id);
      if (result.success && result.data && result.data.length > 0) {
        const completionData = result.data[0];
        
        // If completedBy looks like a user ID (not already formatted), fetch user details
        if (completionData.completedBy && typeof completionData.completedBy === 'string' && 
            completionData.completedBy.length > 20 && /^[a-zA-Z0-9]+$/.test(completionData.completedBy) && 
            !completionData.completedBy.includes('@') && !completionData.completedBy.includes('(')) {
          try {
            const userResult = await userService.getUser(completionData.completedBy);
            if (userResult.success && userResult.data) {
              const userName = userResult.data.name || userResult.data.displayName || userResult.data.email || '';
              const userEmail = userResult.data.email || '';
              if (userEmail) {
                completionData.completedBy = `${userName} (${userEmail})`;
              } else {
                completionData.completedBy = userName || completionData.completedBy;
              }
            }
          } catch (error) {
            console.error(`Error fetching user ${completionData.completedBy}:`, error);
          }
        }
        
        setCompletion(completionData); // Get the most recent completion
      }
    } catch (error) {
      console.error('Error loading completion data:', error);
    } finally {
      setIsLoadingCompletion(false);
    }
  };

  const loadTemplateReferenceDocuments = async () => {
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading template reference documents');
        return;
      }
      
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const foundTemplate = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (foundTemplate) {
          setTemplate(foundTemplate);
          setReferenceDocuments(foundTemplate.referenceDocuments || []);
        } else {
          console.warn('⚠️ Template not found:', { templateId: task.templateId, templateName: task.templateName });
        }
      }
    } catch (error) {
      console.error('Error loading template reference documents:', error);
    }
  };

  const loadChecklistHistory = async () => {
    setIsLoadingHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading checklist history');
        setIsLoadingHistory(false);
        return;
      }
      
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Filter checklists by template ID only - show only checklists associated with this compliance template
          const checklistsResult = await checklistService.getAllChecklists();
          if (checklistsResult.success && checklistsResult.data) {
            const associatedChecklists = checklistsResult.data.filter(c => {
              // Must have a templateId and it must match the task's template
              if (!c.templateId || c.templateId !== template.id) {
                console.log(`[ViewTaskModal] Excluding checklist "${c.name}": templateId mismatch`, {
                  checklistTemplateId: c.templateId,
                  taskTemplateId: template.id
                });
                return false;
              }
              
              // If checklist has locationIds specified, must match task's location
              if (c.locationIds && c.locationIds.length > 0) {
                if (!task.locationId || !c.locationIds.includes(task.locationId)) {
                  console.log(`[ViewTaskModal] Excluding checklist "${c.name}": location mismatch`, {
                    checklistLocationIds: c.locationIds,
                    taskLocationId: task.locationId
                  });
                  return false;
                }
              }
              
              console.log(`[ViewTaskModal] Including checklist "${c.name}" for template "${template.id}"`);
              return true;
            });
            console.log(`[ViewTaskModal] Found ${associatedChecklists.length} checklists for template "${template.id}" (${template.name})`, {
              checklists: associatedChecklists.map(c => ({ name: c.name, templateId: c.templateId }))
            });
            const associatedChecklist = associatedChecklists[0]; // Take first match
            
            if (associatedChecklist) {
              setTemplateChecklist(associatedChecklist);
              
              const historyResult = await checklistCompletionService.getCompletionsForChecklist(associatedChecklist.id!);
              if (historyResult.success && historyResult.data) {
                const filteredRuns = historyResult.data.filter(run => run.locationId === task.locationId);
                setChecklistRuns(filteredRuns);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading checklist history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadRelatedAssets = async () => {
    if (!currentOrganization?.id || !task.locationId || !task.templateId) {
      return;
    }
    
    setIsLoadingAssets(true);
    try {
      // Get all assets for the task's location
      const locationAssetsResult = await assetService.getAssetsByLocation(task.locationId, currentOrganization.id);
      if (!locationAssetsResult.success || !locationAssetsResult.data) {
        setRelatedAssets([]);
        return;
      }

      // Get all asset types to check compliance template associations
      const assetTypesResult = await assetService.getAllAssetTypes(currentOrganization.id);
      if (!assetTypesResult.success || !assetTypesResult.data) {
        setRelatedAssets([]);
        return;
      }

      // Filter assets whose assetType has this task's templateId in complianceTemplateIds
      const assetTypesMap = new Map(assetTypesResult.data.map(at => [at.id!, at]));
      const relatedAssetsList = locationAssetsResult.data.filter(asset => {
        const assetType = assetTypesMap.get(asset.assetTypeId);
        return assetType?.complianceTemplateIds?.includes(task.templateId);
      });

      // Sort assets by name initially
      relatedAssetsList.sort((a, b) => {
        const nameA = (a.assetName || '').toLowerCase();
        const nameB = (b.assetName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setRelatedAssets(relatedAssetsList);

      // OPTIMIZATION: Load completions ONCE for the location, not per asset
      const getDateFromValue = (value: any): Date => {
        if (value instanceof Date) return value;
        if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
          return value.toDate();
        }
        if (typeof value === 'number') return new Date(value);
        return new Date(value);
      };

      // Normalize start date to beginning of day (00:00:00.000)
      const taskCreatedDate = (() => {
        const date = getDateFromValue(task.createdAt);
        date.setHours(0, 0, 0, 0);
        return date;
      })();

      // For completed tasks, use completedAt/completedDate as end date; otherwise use dueDate
      // Normalize end date to end of day (23:59:59.999) to include all completions on that day
      const taskEndDate = (() => {
        const endDateValue = (task as any).completedAt || (task as any).completedDate || task.dueDate;
        const date = getDateFromValue(endDateValue);
        date.setHours(23, 59, 59, 999);
        return date;
      })();

      // Load all completions once, along with checklists and forms to filter by template
      const [allChecklistCompletionsResult, allFormCompletionsResult, checklistsResult, formsResult] = await Promise.all([
        checklistCompletionService.getCompletionsByLocation(task.locationId),
        formCompletionService.getCompletionsByLocation(task.locationId),
        checklistService.getAllChecklists(currentOrganization.id),
        formService.getAllForms(currentOrganization.id)
      ]);

      const allChecklistCompletions = allChecklistCompletionsResult.success && allChecklistCompletionsResult.data 
        ? allChecklistCompletionsResult.data 
        : [];
      const allFormCompletions = allFormCompletionsResult?.data || [];
      
      // Create maps of checklist/form IDs to their templateIds for filtering
      const checklistTemplateMap = new Map<string, string>();
      if (checklistsResult.success && checklistsResult.data) {
        checklistsResult.data.forEach(checklist => {
          if (checklist.id && checklist.templateId) {
            checklistTemplateMap.set(checklist.id, checklist.templateId);
          }
        });
      }
      
      const formTemplateMap = new Map<string, string>();
      if (formsResult.success && formsResult.data) {
        formsResult.data.forEach(form => {
          if (form.id && form.templateId) {
            formTemplateMap.set(form.id, form.templateId);
          }
        });
      }

      // Filter completions by date range AND template ID
      const dateFilteredChecklistCompletions = allChecklistCompletions.filter(cc => {
        // First filter by date range - only include completions within task period
        const completionDate = cc.completedAt?.toDate ? cc.completedAt.toDate() :
          (typeof cc.completedAt === 'number' ? new Date(cc.completedAt) :
          (cc.completedAt instanceof Date ? cc.completedAt : new Date()));
        
        // Exclude completions before task creation or after task end (completedAt or dueDate)
        if (completionDate < taskCreatedDate || completionDate > taskEndDate) {
          return false;
        }
        
        // Then filter by template ID - only include completions from checklists that match the task's template
        if (cc.checklistId) {
          const checklistTemplateId = checklistTemplateMap.get(cc.checklistId);
          if (checklistTemplateId === task.templateId) {
            return true;
          } else {
            console.log(`[ViewTaskModal] Excluding checklist completion for asset - template mismatch`, {
              checklistId: cc.checklistId,
              checklistTemplateId,
              taskTemplateId: task.templateId
            });
            return false;
          }
        }
        
        // If no checklistId, exclude it
        return false;
      });

      const dateFilteredFormCompletions = allFormCompletions.filter(fc => {
        // First filter by date range - only include completions within task period
        const completionDate = fc.completionDate?.toDate ? fc.completionDate.toDate() :
          (typeof fc.completionDate === 'number' ? new Date(fc.completionDate) :
          (fc.completionDate instanceof Date ? fc.completionDate : new Date()));
        
        // Exclude completions before task creation or after task end (completedAt or dueDate)
        if (completionDate < taskCreatedDate || completionDate > taskEndDate) {
          return false;
        }
        
        // Then filter by template ID - only include completions from forms that match the task's template
        if (fc.formId) {
          const formTemplateId = formTemplateMap.get(fc.formId);
          if (formTemplateId === task.templateId) {
            return true;
          } else {
            console.log(`[ViewTaskModal] Excluding form completion for asset - template mismatch`, {
              formId: fc.formId,
              formTemplateId,
              taskTemplateId: task.templateId
            });
            return false;
          }
        }
        
        // If no formId, exclude it
        return false;
      });

      // Create asset ID set for quick lookup
      const assetIds = new Set(relatedAssetsList.filter(a => a.id).map(a => a.id!));

      // Group completions by asset ID
      const completionsByAssetId = new Map<string, {
        checklistCompletions: ChecklistCompletion[];
        formCompletions: FormCompletion[];
      }>();

      // Initialize all assets with empty completions
      relatedAssetsList.forEach(asset => {
        if (asset.id) {
          completionsByAssetId.set(asset.id, {
            checklistCompletions: [],
            formCompletions: []
          });
        }
      });

      // Distribute checklist completions to assets
      dateFilteredChecklistCompletions.forEach(cc => {
        const assetId = (cc as any).assetId || 
                       (cc.variableValues && cc.variableValues['assetId']) ||
                       (cc.variableValues && cc.variableValues['asset']);
        if (assetId && assetIds.has(assetId)) {
          const existing = completionsByAssetId.get(assetId);
          if (existing) {
            existing.checklistCompletions.push(cc);
          }
        }
      });

      // Distribute form completions to assets
      dateFilteredFormCompletions.forEach(fc => {
        const assetId = (fc as any).assetId ||
                       (fc.fieldValues && fc.fieldValues['assetId']) ||
                       (fc.fieldValues && fc.fieldValues['asset']);
        if (assetId && assetIds.has(assetId)) {
          const existing = completionsByAssetId.get(assetId);
          if (existing) {
            existing.formCompletions.push(fc);
          }
        }
      });

      // Build completions data array
      const completionsData = relatedAssetsList
        .filter(asset => asset.id)
        .map(asset => {
          const completions = completionsByAssetId.get(asset.id!) || {
            checklistCompletions: [],
            formCompletions: []
          };
          return {
            assetId: asset.id!,
            assetName: asset.assetName,
            checklistCompletions: completions.checklistCompletions,
            formCompletions: completions.formCompletions
          };
        });

      setAssetCompletions(completionsData);
    } catch (error) {
      console.error('Error loading related assets:', error);
      setRelatedAssets([]);
      setAssetCompletions([]);
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const openFile = (url: string) => {
    window.open(url, '_blank');
  };

  const handleTaskCompletion = async (completionData: TaskCompletionData) => {
    if (!task || !user?.id) return;
    try {
      const result = await completeTask(task.id, user.id);
      if (!result.success) {
        showNotification(result.error || 'Failed to submit task for review', 'error');
        return;
      }
      showNotification('Task submitted for review!', 'success');
      setShowCompleteModal(false);
      onTaskUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error completing task:', error);
      showNotification('Failed to complete task', 'error');
    }
  };

  const handleReviewComplete = async () => {
    setShowReviewModal(false);
    onTaskUpdated?.();
    onClose();
  };

  const formatDate = (date: any) => {
    try {
      if (date?.toDate) {
        return date.toDate().toLocaleString();
      } else if (date instanceof Date) {
        return date.toLocaleString();
      } else if (typeof date === 'number') {
        return new Date(date).toLocaleString();
      } else {
        return new Date(date).toLocaleString();
      }
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120] p-4">
        {/* Backdrop overlay when run details panel is open - fades the background and main modal */}
        {selectedRun && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-20 z-[51]"
            onClick={() => setSelectedRun(null)}
          />
        )}
        <div className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto transition-all relative z-[52] ${
          selectedRun ? 'max-w-2xl opacity-60' : 'max-w-3xl'
        }`}>
          <div className="p-3 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Task Details</h3>
                <p className="text-xs text-gray-600 mt-0.5">{task.templateName}</p>
                <p className="text-[11px] text-gray-500 mt-1 font-mono">
                  Task ID: {task.id} • Start: {task.createdAt?.toDate ? task.createdAt.toDate().toLocaleDateString() : (task.createdAt instanceof Date ? task.createdAt.toLocaleDateString() : (typeof task.createdAt === 'number' ? new Date(task.createdAt).toLocaleDateString() : '—'))}
                </p>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* Rejection Alert - Show prominently if task was rejected */}
            {task.rejectedBy && task.rejectionReason && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-900 mb-1 flex items-center gap-2">
                      Task Previously Rejected
                    </h4>
                    <div className="space-y-1 text-xs text-red-800">
                      <div>
                        <span className="font-medium">Rejected by:</span> {task.rejectedBy}
                      </div>
                      {task.rejectedAt && (
                        <div>
                          <span className="font-medium">Rejected on:</span>{' '}
                          {(() => {
                            try {
                              const rejectedDate = task.rejectedAt?.toDate ? task.rejectedAt.toDate() : 
                                                  (task.rejectedAt instanceof Date ? task.rejectedAt :
                                                  (typeof task.rejectedAt === 'number' ? new Date(task.rejectedAt) : new Date(task.rejectedAt)));
                              return rejectedDate.toLocaleString();
                            } catch (error) {
                              return 'Invalid Date';
                            }
                          })()}
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-t border-red-200">
                        <span className="font-medium">Reason for rejection:</span>
                        <p className="mt-1 text-red-900 bg-red-100 p-2 rounded border border-red-200">
                          {task.rejectionReason}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reference Images */}
            {task.referenceImageUrls && task.referenceImageUrls.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                  <Image className="w-3 h-3 text-blue-600" />
                  Reference Images
                </h4>
                <ImageGallery
                  images={task.referenceImageUrls}
                  imageNames={task.referenceImageFileNames}
                  maxColumns={3}
                  showDownload={true}
                />
              </div>
            )}

            {/* Reference Documents */}
            {referenceDocuments.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                  <FileText className="w-3 h-3 text-blue-600" />
                  Reference Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {referenceDocuments.map((doc, index) => (
                    <a
                      key={index}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-1.5 bg-white rounded border border-blue-200 hover:border-blue-400 transition-all cursor-pointer text-xs"
                    >
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {doc.type === 'pdf' ? (
                          <FileText className="w-3 h-3 text-red-600 flex-shrink-0" />
                        ) : (
                          <Image className="w-3 h-3 text-green-600 flex-shrink-0" />
                        )}
                        <span className="text-gray-900 truncate" title={doc.name}>
                          {doc.name}
                        </span>
                        <span className="text-gray-500 flex-shrink-0">
                          ({doc.type.toUpperCase()})
                        </span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-blue-600 flex-shrink-0 ml-1" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Task Details */}
            <div className="bg-gray-50 p-2 rounded-lg space-y-1.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-xs text-gray-500">Location</span>
                  <p className="text-xs font-medium text-gray-900">{task.locationName}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Category</span>
                  <p className="text-xs font-medium text-gray-900">{task.category}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Owner Role</span>
                  <p className="text-xs font-medium text-gray-900">{task.ownerRole}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Risk Level</span>
                  <p className="text-xs font-medium text-gray-900">{task.riskLevel}</p>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Pass/Fail Criteria</span>
                <p className="text-xs text-gray-900 mt-0.5">{task.passFailCriteria}</p>
              </div>
            </div>

            {/* Completion Details */}
            {isLoadingCompletion ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-xs text-gray-500 mt-2">Loading completion details...</p>
              </div>
            ) : completion ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-1.5">Completion Details</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-xs text-gray-500">Completed By</span>
                      <p className="text-xs font-medium text-gray-900">{(completion.completedByName || completion.completedBy || task.completedBy) || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Completion Date</span>
                      <p className="text-xs font-medium text-gray-900">{formatDate(completion.completionDate)}</p>
                    </div>
                    {task.reviewedBy && (
                      <>
                        <div>
                          <span className="text-xs text-gray-500">Reviewed By</span>
                          <p className="text-xs font-medium text-gray-900">{task.reviewedBy || completion.reviewedBy || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Reviewed Date</span>
                          <p className="text-xs font-medium text-gray-900">
                            {(() => {
                              try {
                                let reviewDate: any = task.reviewedAt || completion.reviewedAt;
                                if (!reviewDate) return 'N/A';
                                
                                let date: Date;
                                if (reviewDate?.toDate) {
                                  date = reviewDate.toDate();
                                } else if (reviewDate instanceof Date) {
                                  date = reviewDate;
                                } else if (typeof reviewDate === 'number') {
                                  date = new Date(reviewDate);
                                } else {
                                  date = new Date(reviewDate);
                                }
                                
                                return date.toLocaleString();
                              } catch (error) {
                                return 'Invalid Date';
                              }
                            })()}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Comments */}
                {completion.comments && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comments</label>
                    <div className="bg-gray-50 p-2 rounded text-xs text-gray-900 border border-gray-200">
                      {completion.comments}
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {completion.notes && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Additional Notes</label>
                    <div className="bg-gray-50 p-2 rounded text-xs text-gray-900 border border-gray-200">
                      {completion.notes}
                    </div>
                  </div>
                )}

                {/* Electronic Signature */}
                {completion.signature && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Electronic Signature</label>
                    <div className="bg-gray-50 p-2 rounded border border-gray-200">
                      <div className="space-y-2">
                        {(completion.signature.signatureUrl || completion.signature.signatureDataUrl) && (
                          <div className="bg-white p-2 rounded border border-gray-300">
                            <img
                              src={completion.signature.signatureUrl || completion.signature.signatureDataUrl}
                              alt="Signature"
                              className="max-w-full h-auto border border-gray-200 rounded"
                              style={{ maxHeight: '150px' }}
                            />
                          </div>
                        )}
                        <div className="text-xs space-y-0.5">
                          {completion.signature.signerName && (
                            <div>
                              <span className="font-medium text-gray-700">Signed by:</span>{' '}
                              <span className="text-gray-900">{completion.signature.signerName}</span>
                            </div>
                          )}
                          {completion.signature.signerEmail && (
                            <div>
                              <span className="font-medium text-gray-700">Email:</span>{' '}
                              <span className="text-gray-900">{completion.signature.signerEmail}</span>
                            </div>
                          )}
                          {completion.signature.signedAt && (
                            <div>
                              <span className="font-medium text-gray-700">Signed at:</span>{' '}
                              <span className="text-gray-900">
                                {completion.signature.signedAt instanceof Date
                                  ? completion.signature.signedAt.toLocaleString()
                                  : typeof completion.signature.signedAt === 'number'
                                  ? new Date(completion.signature.signedAt).toLocaleString()
                                  : new Date(completion.signature.signedAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Evidence Files */}
                {completion.evidenceUrls && completion.evidenceUrls.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Uploaded Evidence Files</label>
                    <div className="space-y-1">
                      {completion.evidenceUrls.map((url, idx) => {
                        const fileName = completion.evidenceFileNames?.[idx] || `Evidence File ${idx + 1}`;
                        const analysis = completion.documentAnalyses?.[fileName];
                        const fileNote = completion.fileNotes?.[fileName];
                        
                        if (url.startsWith('UPLOAD_FAILED:')) {
                          return (
                            <div key={idx} className="flex items-center justify-between p-1.5 bg-red-50 rounded text-xs border border-red-200">
                              <div className="flex items-center gap-1.5">
                                <AlertCircle className="w-3 h-3 text-red-600" />
                                <span className="text-red-900">Upload Failed: {url.replace('UPLOAD_FAILED:', '')}</span>
                              </div>
                            </div>
                          );
                        }
                        const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) || url.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
                        
                        return (
                          <div key={idx} className="p-2 bg-gray-50 rounded text-xs border border-gray-200">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <Paperclip className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                <span className="text-gray-900 truncate">{fileName}</span>
                                {analysis && (
                                  <Sparkles className="w-3 h-3 text-purple-600 flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  onClick={() => openFile(url)}
                                  className="btn-primary text-xs px-2 py-0.5 flex items-center gap-1"
                                  title="View File"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View
                                </button>
                                <a
                                  href={url}
                                  download
                                  className="btn-secondary text-xs px-2 py-0.5 flex items-center gap-1"
                                  title="Download File"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              </div>
                            </div>
                            
                            {/* Image Preview and Analysis Layout */}
                            <div className={`flex gap-2 ${isImage ? 'flex-row' : 'flex-col'}`}>
                              {/* Image Preview (Left) */}
                              {isImage && (
                                <div className="flex-shrink-0 w-32 h-32 border border-gray-300 rounded bg-white flex items-center justify-center overflow-hidden">
                                  <img 
                                    src={url} 
                                    alt={fileName}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => {
                                      // Hide image if it fails to load
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Analysis and Notes (Right) */}
                              <div className={`flex-1 min-w-0 ${isImage ? '' : 'mt-1.5'}`}>
                                {/* Document Analysis Results */}
                                {analysis && (
                                  <div className="mt-1.5 p-1.5 bg-white rounded border border-purple-200">
                                <div className="text-xs space-y-1">
                                  <div>
                                    <span className="font-medium text-purple-700">Type:</span>{' '}
                                    <span className="text-gray-900">{analysis.documentType || 'Unknown'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-purple-700">Summary:</span>{' '}
                                    <span className="text-gray-900">{analysis.summary || 'No summary available'}</span>
                                  </div>
                                  {analysis.expiryDate && (
                                    <div className="bg-orange-50 border border-orange-200 rounded p-1 mt-1">
                                      <span className="font-medium text-orange-700">Expiry Date Found:</span>{' '}
                                      <span className="text-gray-900">
                                        {typeof analysis.expiryDate === 'string' 
                                          ? new Date(analysis.expiryDate).toLocaleDateString()
                                          : analysis.expiryDate.toLocaleDateString()}
                                      </span>
                                    </div>
                                  )}
                                  {analysis.extractedDates && analysis.extractedDates.length > 0 && (
                                    <div className="mt-1">
                                      <span className="font-medium text-purple-700">All Dates:</span>
                                      <div className="space-y-0.5 mt-0.5">
                                        {analysis.extractedDates.map((dateInfo: any, dIdx: number) => {
                                          const date = typeof dateInfo.date === 'string' 
                                            ? new Date(dateInfo.date) 
                                            : dateInfo.date;
                                          return (
                                            <div key={dIdx} className="text-gray-700 text-xs">
                                              {dateInfo.type}: {date.toLocaleDateString()} ({dateInfo.confidence} confidence)
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  {analysis.keyInformation && Object.keys(analysis.keyInformation).length > 0 && (
                                    <div className="mt-1">
                                      <span className="font-medium text-purple-700">Key Info:</span>
                                      <div className="space-y-0.5 mt-0.5">
                                        {Object.entries(analysis.keyInformation).map(([key, value]) => {
                                          // Convert value to string if it's an object
                                          let displayValue: string;
                                          if (value === null || value === undefined) {
                                            displayValue = 'N/A';
                                          } else if (typeof value === 'object') {
                                            displayValue = JSON.stringify(value);
                                          } else {
                                            displayValue = String(value);
                                          }
                                          return (
                                            <div key={key} className="text-gray-700 text-xs">
                                              {key}: {displayValue}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  </div>
                                </div>
                              )}
                              
                              {/* File Notes */}
                              {fileNote && (
                                <div className="mt-1.5 p-1.5 bg-blue-50 rounded border border-blue-200">
                                  <div className="text-xs">
                                    <span className="font-medium text-blue-700">Notes:</span>
                                    <div className="text-gray-900 mt-0.5 whitespace-pre-wrap">{fileNote}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="text-xs">No completion data found for this task.</p>
              </div>
            )}

            {/* Related Assets with Completed Work */}
            {relatedAssets.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-primary-600" />
                  Related Assets ({relatedAssets.length} {relatedAssets.length === 1 ? 'asset' : 'assets'})
                </h4>
                
                {isLoadingAssets ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-2">Loading assets and completions...</p>
                  </div>
                ) : (() => {
                  // Prepare assets with completions data
                  const assetsWithCompletions = relatedAssets.map(asset => {
                    const assetComp = assetCompletions.find(ac => ac.assetId === asset.id) || {
                      assetId: asset.id!,
                      assetName: asset.assetName,
                      checklistCompletions: [],
                      formCompletions: []
                    };
                    
                    // Combine all checklists and forms
                    const allCompletions = [
                      ...assetComp.checklistCompletions.map(cc => ({
                        type: 'checklist' as const,
                        id: cc.id || '',
                        name: cc.checklistName,
                        completionDate: cc.completedAt,
                        completedBy: cc.completedBy,
                        data: cc
                      })),
                      ...assetComp.formCompletions.map(fc => ({
                        type: 'form' as const,
                        id: fc.id || '',
                        name: fc.formName,
                        completionDate: fc.completionDate,
                        completedBy: fc.completedBy,
                        data: fc
                      }))
                    ];
                    
                    return {
                      asset,
                      assetComp,
                      allCompletions
                    };
                  });

                  return (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border-collapse border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                              Asset Name
                            </th>
                            <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                              Checklists & Forms
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                        {assetsWithCompletions.map(({ asset, allCompletions }) => {
                          return (
                            <tr key={asset.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900">
                                {asset.assetName}
                                {asset.assetLocation && (
                                  <span className="text-xs text-gray-500 ml-2">({asset.assetLocation})</span>
                                )}
                              </td>
                              <td className="px-4 py-3 border border-gray-200">
                                {allCompletions.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {allCompletions.map((completion, idx) => (
                                      <span
                                        key={idx}
                                        className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded flex items-center gap-1"
                                      >
                                        {completion.type === 'checklist' ? (
                                          <ClipboardList className="w-3 h-3" />
                                        ) : (
                                          <FileText className="w-3 h-3" />
                                        )}
                                        {completion.name}
                                        {completion.completionDate && (
                                          <span className="text-blue-600 ml-1">
                                            ({completion.completionDate?.toDate ? 
                                              completion.completionDate.toDate().toLocaleDateString() :
                                              (typeof completion.completionDate === 'number' ? 
                                                new Date(completion.completionDate).toLocaleDateString() :
                                                (completion.completionDate instanceof Date ? 
                                                  completion.completionDate.toLocaleDateString() : ''))})
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No completions</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              {showActions && (task.status === 'pending' || task.status === 'overdue') && (
                <button
                  onClick={() => setShowCompleteModal(true)}
                  className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5"
                  title="Submit task for review"
                >
                  <Check className="w-4 h-4" />
                  Complete
                </button>
              )}
              {showActions && task.status === 'pending_review' && canReviewTasks && (
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="bg-orange-600 text-white hover:bg-orange-700 px-4 py-2 text-xs rounded flex items-center gap-1.5"
                  title="Review and approve task"
                >
                  <CheckCircle className="w-4 h-4" />
                  Review
                </button>
              )}
              <button
                onClick={onClose}
                className="btn-secondary px-4 py-2 text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>

        {/* Checklist Run Details Panel */}
        {selectedRun && templateChecklist && (
          <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[60] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Checklist Run Details</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {formatDate(selectedRun.completedAt)}
                </p>
              </div>
              <button
                onClick={() => setSelectedRun(null)}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {/* Variables */}
              {selectedRun.variableValues && Object.keys(selectedRun.variableValues).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                  <div className="space-y-1">
                    {Object.entries(selectedRun.variableValues).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="font-medium text-gray-700">{key}:</span>{' '}
                        <span className="text-gray-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completion Info */}
              <div className="bg-gray-50 border border-gray-200 rounded p-2">
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium text-gray-700">Location:</span>{' '}
                    <span className="text-gray-900">{selectedRun.locationName || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Completed By:</span>{' '}
                    <span className="text-gray-900">{selectedRun.completedBy || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Checklist Items */}
              <div>
                <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                  Checklist Items ({Object.values(selectedRun.items || {}).filter(v => v).length}/{templateChecklist.items?.length || 0} completed)
                </h4>
                <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {templateChecklist.items?.map((item, index) => {
                    // Use items (mobile format)
                    const items = selectedRun.items || {};
                    const isCompleted = items[item.text] || false;
                    const photoData = selectedRun.photos?.[item.text];
                    // Normalize photos to array: handle both string (single photo) and string[] (multiple photos)
                    const photos = photoData 
                      ? (Array.isArray(photoData) ? photoData : [photoData])
                      : [];
                    const taskRefs = (selectedRun.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                    
                    return (
                      <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                        <div className="flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-900">
                            <span className="font-medium">{index + 1}.</span> {item.text}
                          </div>
                          {isCompleted && taskRefs.length > 0 && (
                            <div className="mt-1 text-xs text-blue-600 font-medium">
                              ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                            </div>
                          )}
                          {photos.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-2">
                              {photos.map((photoUrl, photoIndex) => (
                                <img 
                                  key={photoIndex}
                                  src={photoUrl} 
                                  alt={`${item.text} photo ${photoIndex + 1}`}
                                  className="max-w-full h-auto rounded border border-gray-300 max-h-32"
                                  onClick={() => window.open(photoUrl, '_blank')}
                                  style={{ cursor: 'pointer' }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Complete Task Modal - shown when user clicks Complete */}
        {showCompleteModal && (
          <CompleteTaskModal
            task={task as unknown as ComplianceTask}
            onComplete={handleTaskCompletion}
            onClose={() => setShowCompleteModal(false)}
          />
        )}

        {/* Review Task Modal - shown when admin/manager clicks Review on pending_review task */}
        {showReviewModal && (
          <ReviewTaskModal
            task={task}
            onClose={() => setShowReviewModal(false)}
            onReview={handleReviewComplete}
          />
        )}
      </div>
    </>
  );
};

export default ViewTaskModal;
